import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CaError, CaFormFieldComponent, CaLabel } from '@global-front-components/ui/form-field';
import { CaInputDirective } from '@global-front-components/ui/input';
import { TranslateModule } from '@ngx-translate/core';
import { Persona, TipoDocumento } from '../../model/poliza.model';
import { DateUtilsService } from '../../services/util/date-utils.service';
import { DatosPersonalesConfig, ModoEdicion } from '../../../ui/model/catalogo-config.model';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CaDatepickerComponent } from '@global-front-components/ui/datepicker';
import { CaSelectComponent } from '@global-front-components/ui/select';
import { CatalogoGeneralStoreService } from '../../services/store/catalogo-general-store.service';
import { ModoEdicionUtils } from '../../util/modo-edicion.utils';
import { of, Subject, switchMap } from 'rxjs';

@Component({
	selector: 'app-datos-personales',
	standalone: true,
	imports: [
		CommonModule,
		CaLabel,
		CaInputDirective,
		CaFormFieldComponent,
		CaDatepickerComponent,
		CaSelectComponent,
		CaError,
		ReactiveFormsModule,
		TranslateModule
	],
	templateUrl: './datos-personales.component.html',
	styleUrls: ['./datos-personales.component.scss']
})
export class DatosPersonalesComponent implements OnInit, OnChanges {
	@Input() config: DatosPersonalesConfig;
	@Input() form: FormGroup;
	@Output() personaChange = new EventEmitter<Persona>();
	readonly idTipoDocumentoCIF = 2;

	persona: Persona;
	prefijoPersona: string;
	tiposDocumento: TipoDocumento[] = [];
	idTipoDocumentoSeleccionado: number | null = null;

	mostrarDocumento = false;
	mostrarNombre = false;
	mostrarFechaNacimiento = false;
	mostrarSexo = false;
	modoEdicion: ModoEdicion;

	constructor(
		private fb: FormBuilder,
		private catalogoGeneralStore: CatalogoGeneralStoreService,
		private dateUtil: DateUtilsService) {
	}

	mostrarFecha(date: Date | null): string {
		return this.dateUtil.formatearFecha(date);
	}

	ngOnInit(): void {
		this.prefijoPersona = this.config?.rol ? `${this.config.rol}_` : '';
		this.mostrarDocumento = this.config?.mostrarDocumento;
		this.mostrarNombre = this.config?.mostrarNombre;
		this.mostrarFechaNacimiento = this.config?.mostrarFechaNacimiento;
		this.modoEdicion = this.config?.modoEdicion || ModoEdicion.SI;
		this.mostrarSexo = this.config?.mostrarSexo;

		if (!this.form) {
			this.form = this.fb.group({});
		}
		this.buildForm();

		if (this.config?.mostrarDocumento) {
			this.cargarValidacionesTipoDoccumento();
		}

		this.form.valueChanges.subscribe(() => {
			this.emitirPersona();
		});
	}

	private cargarValidacionesTipoDoccumento() {
		const docControl = this.form.get(this.prefijoPersona + 'documento');
		const tipoDocControl = this.form.get(this.prefijoPersona + 'tipo-documento');

		this.catalogoGeneralStore.getTiposDocumento().subscribe(tiposDocumento => {

			this.tiposDocumento = tiposDocumento || [];

			const docInicial = this.config?.persona?.documento;
			if (docInicial) {
				this.catalogoGeneralStore.getIdTipoDocumento(docInicial).subscribe(tipoId => {
					this.idTipoDocumentoSeleccionado = tipoId ?? null;
					tipoDocControl.setValue(tipoId ?? -1, { emitEvent: false });
					if (this.mostrarNombre) {
						this.toggleControlsByTipo(tipoId);
					}
				});
			}
			if (this.modoEdicion !== ModoEdicion.SI) {
				tipoDocControl.disable();
			} else {
				tipoDocControl.valueChanges
					.subscribe((tipoId) => {
						this.idTipoDocumentoSeleccionado = tipoId ?? null;
						if (this.mostrarNombre) {
							this.toggleControlsByTipo(tipoId);
						}
					});
			}
			//Cuando se pierde el foco del campo documento, se busca el tipo de documento
			docControl.valueChanges
				.pipe(switchMap(v => {
					const val = (v || '').trim(); return val ? this.catalogoGeneralStore.getIdTipoDocumento(val) : of(null);
				})).subscribe(tipoId => {
					this.idTipoDocumentoSeleccionado = tipoId ?? null;
					tipoDocControl.setValue(tipoId ?? -1, { emitEvent: false });
					if (this.mostrarNombre) {
						this.toggleControlsByTipo(tipoId);
					}
				});
		});
	}

	buildForm() {
		const persona = this.config?.persona || {};

		if (this.config?.mostrarDocumento) {
			this.form.addControl(
				this.prefijoPersona + 'documento',
				this.fb.control(
					{ value: persona.documento || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.documento) },
					{
						validators: [Validators.required],
						updateOn: 'blur'
					}
				)
			);
			this.form.addControl(this.prefijoPersona + 'tipo-documento',
				this.fb.control(
					{ value: '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.documento) },
					[Validators.required]
				));
		}

		if (this.mostrarNombre) {
			if (persona.razonSocial && persona.razonSocial !== '') {
				this.form.addControl(this.prefijoPersona + 'razon-social',
					this.fb.control(
						{ value: persona.razonSocial || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.nombre) },
						[Validators.required]
					));
			} else {
				this.form.addControl(this.prefijoPersona + 'nombre',
					this.fb.control(
						{ value: persona.nombre || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.nombre) },
						[Validators.required]
					));
				this.form.addControl(this.prefijoPersona + 'apellido1',
					this.fb.control(
						{ value: persona.apellido1 || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.apellido1) },
						[Validators.required]
					));
				this.form.addControl(this.prefijoPersona + 'apellido2',
					this.fb.control(
						{ value: persona.apellido2 || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.apellido2) }
					));
			}

		}

		if (this.mostrarFechaNacimiento) {
			this.form.addControl(this.prefijoPersona + 'fechaNacimiento',
				this.fb.control(
					{ value: persona.fechaNacimiento || null, disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.fechaNacimiento) }
				));
		}

		if (this.mostrarSexo) {
			this.form.addControl(this.prefijoPersona + 'codigoSexo',
				this.fb.control(
					{ value: persona.codigoSexo || '', disabled: ModoEdicionUtils.campoDeshabilitado(this.modoEdicion, persona.codigoSexo) }
				));
		}
	}

	private toggleControlsByTipo(tipoId: number | null) {
		const nombre = this.prefijoPersona + 'nombre';
		const apellido1 = this.prefijoPersona + 'apellido1';
		const apellido2 = this.prefijoPersona + 'apellido2';
		const razonSocial = this.prefijoPersona + 'razon-social';

		if (tipoId === this.idTipoDocumentoCIF) {
			// CIF: eliminar nombre/apellidos, añadir razón social
			[nombre, apellido1, apellido2].forEach(ctrl => {
				if (this.form.contains(ctrl)) this.form.removeControl(ctrl);
			});
			if (!this.form.contains(razonSocial)) {
				this.form.addControl(
					razonSocial,
					this.fb.control('', Validators.required)
				);
			}
		} else {
			// No CIF: eliminar razón social, añadir nombre/apellidos
			if (this.form.contains(razonSocial)) {
				this.form.removeControl(razonSocial);
			}
			[nombre, apellido1].forEach(ctrl => {
				if (!this.form.contains(ctrl)) {
					this.form.addControl(
						ctrl,
						this.fb.control('', Validators.required)
					);
				}
			});
			this.form.addControl(apellido2, this.fb.control(''));
		}
	}

	emitirPersona(): void {
		const get = (controlName: string) => this.form.get(controlName)?.value;

		const esCIF = this.idTipoDocumentoSeleccionado === this.idTipoDocumentoCIF;

		const persona: Persona = {
			documento: get(this.prefijoPersona + 'documento')?.toUpperCase(),
			nombre: esCIF ? null : get(this.prefijoPersona + 'nombre')?.toUpperCase(),
			apellido1: esCIF ? null : get(this.prefijoPersona + 'apellido1')?.toUpperCase(),
			apellido2: esCIF ? null : get(this.prefijoPersona + 'apellido2')?.toUpperCase(),
			razonSocial: esCIF ? get(this.prefijoPersona + 'razon-social')?.toUpperCase() : null,
			fechaNacimiento: get(this.prefijoPersona + 'fechaNacimiento'),
			codigoSexo: get(this.prefijoPersona + 'codigoSexo')
		};
		this.personaChange.emit(persona);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['config'] && !changes['config'].firstChange) {
			this.ngOnInit();
		}
	}
}
